/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { User } from '/imports/ui/Types/user';
import useCurrentUser from '/imports/ui/core/hooks/useCurrentUser';
import useMeeting from '/imports/ui/core/hooks/useMeeting';
import { Meeting } from '/imports/ui/Types/meeting';
import { useMutation, useReactiveVar } from '@apollo/client';
import AudioModalContainer from '../../audio-modal/container';
import AudioManager from '/imports/ui/services/audio-manager';
import Styled from './styles';
import InputStreamLiveSelectorContainer from './input-stream-live-selector/component';
import { UPDATE_ECHO_TEST_RUNNING } from './queries';
import connectionStatus from '/imports/ui/core/graphql/singletons/connectionStatus';

const intlMessages = defineMessages({
  joinAudio: {
    id: 'app.audio.joinAudio',
    description: 'Join audio button label',
  },
});

interface AudioControlsProps {
  inAudio: boolean;
  isConnected: boolean;
  disabled: boolean;
  isEchoTest: boolean;
  updateEchoTestRunning: () => void;
  away: boolean;
  isConnecting?: boolean;
}

const AudioControls: React.FC<AudioControlsProps> = ({
  isConnected,
  isEchoTest,
  updateEchoTestRunning,
  isConnecting,
}) => {
  const intl = useIntl();
  const echoTestIntervalRef = React.useRef<ReturnType<typeof setTimeout>>();

  const [isAudioModalOpen, setIsAudioModalOpen] = React.useState(false);
  const [audioModalContent, setAudioModalContent] = React.useState<string | null>(null);
  const [audioModalProps, setAudioModalProps] = React.useState<{ unmuteOnExit?: boolean } | null>(null);

  const openAudioSettings = (props: { unmuteOnExit?: boolean } = {}) => {
    setAudioModalContent('settings');
    setAudioModalProps(props);
    setIsAudioModalOpen(true);
  };

  const openAudioModal = () => {
    setAudioModalContent(null);
    setAudioModalProps(null);
    setIsAudioModalOpen(true);
  };

  React.useEffect(() => {
    setIsAudioModalOpen(true);
  }, []);

  useEffect(() => {
    if (isEchoTest) {
      echoTestIntervalRef.current = setInterval(() => {
        updateEchoTestRunning();
      }, 1000);
    } else {
      clearInterval(echoTestIntervalRef.current);
    }
  }, [isEchoTest]);

  const showJoinButton = !isConnected && !isConnecting && !isAudioModalOpen;

  return (
    <Styled.Container>
      {showJoinButton ? (
        <Styled.JoinAudioButton
          onClick={openAudioModal}
          label={intl.formatMessage(intlMessages.joinAudio)}
          icon="unmute"
          color="default"
          ghost
          circle={false}
          hideLabel={false}
          size="lg"
          data-test="joinAudioBtn"
        />
      ) : (
        <InputStreamLiveSelectorContainer openAudioSettings={openAudioSettings} />
      )}
      {isAudioModalOpen && (
        <AudioModalContainer
          priority="low"
          setIsOpen={() => {
            setIsAudioModalOpen(false);
            setAudioModalContent(null);
            setAudioModalProps(null);
          }}
          isOpen={isAudioModalOpen}
          content={audioModalContent}
          unmuteOnExit={audioModalProps?.unmuteOnExit}
        />
      )}
    </Styled.Container>
  );
};

export const AudioControlsContainer: React.FC = () => {
  const { data: currentUser } = useCurrentUser((u: Partial<User>) => ({
    presenter: u.presenter,
    isModerator: u.isModerator,
    locked: u?.locked ?? false,
    voice: u.voice,
    away: u.away,
  }));

  const { data: currentMeeting } = useMeeting((m: Partial<Meeting>) => ({
    lockSettings: m.lockSettings,
  }));
  const [updateEchoTestRunning] = useMutation(UPDATE_ECHO_TEST_RUNNING);

  // I access the internal variable to get the makevar reference,
  // so we doesn't broke the client that uses the value directly
  // and I can use it to make my component reactive

  // @ts-ignore - temporary while hybrid (meteor+GraphQl)
  const isConnected = useReactiveVar(AudioManager._isConnected.value) as boolean;
  // @ts-ignore - temporary while hybrid (meteor+GraphQl)
  const isConnecting = useReactiveVar(AudioManager._isConnecting.value) as boolean;
  // @ts-ignore - temporary while hybrid (meteor+GraphQl)
  const isHangingUp = useReactiveVar(AudioManager._isHangingUp.value) as boolean;
  // @ts-ignore - temporary while hybrid (meteor+GraphQl)
  const isEchoTest = useReactiveVar(AudioManager._isEchoTest.value) as boolean;

  const isClientConnected = useReactiveVar(connectionStatus.getConnectedStatusVar());

  if (!currentUser || !currentMeeting) return null;

  return (
    <AudioControls
      inAudio={!!currentUser?.voice}
      isConnected={isConnected}
      disabled={(isConnecting || isHangingUp || !isClientConnected)}
      isEchoTest={isEchoTest}
      updateEchoTestRunning={updateEchoTestRunning}
      away={currentUser.away || false}
      isConnecting={isConnecting}
    />
  );
};

export default AudioControlsContainer;
